import {
    Assignment,
    ChevronLeft,
    DateRange,
    EventBusy,
    ExitToApp,
    FormatListNumbered,
    LocalHospital,
    ManageAccounts,
    Map,
    Monitor,
    People,
    PinDrop,
    PostAdd,
    Route,
    Settings,
} from '@mui/icons-material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import {
    AppBar,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SwipeableDrawer,
    Toolbar,
    Typography,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/styles';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { FC, Fragment, MouseEvent, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router';

import axleIconSrc from '../../assets/icon.png';
import axleLogoSrc from '../../assets/logoWithoutHealth.png';
import ProfessionalIcon from '../../assets/qhpIcon.svg?react';
import { useLogout, useUserEmails } from '../../auth/authentication';
import { AXLE_TEAL } from '../../constants/colors';
import { useAppConfig } from '../../fetching/api/appConfig';
import { useShowDemandConnections } from '../../pages/DemandPage/DemandConnections/DemandConnections';
import { useShowSupplyConnections } from '../../pages/SupplyPage/SupplyConnections/SupplyConnections';
import { APP_BAR_Z_INDEX } from '../../theme';
import { FeatureFlag } from '../../types/featureFlag';
import { CommandFInvisibleText } from '../../utils/CommandFInvisibleText';
import { useBooleanState, useBooleanStateAtom } from '../../utils/booleanState';
import { useAnimatedVisibility, useTransition } from '../../utils/cssTransitions';
import { useIsMobile } from '../../utils/mediaBreakpoints';
import { DefiniteComponent, ReturnComponent } from '../../utils/reactTypes';
import { useFeatureFlagEnabled } from '../../utils/useFeatureFlagEnabled';
import { SvgIcon } from '../images';
import { useReserveRootNavOffset } from '../layout/rootNavOffset';
import { centerChildren } from '../utils/centering';
import { ConditionalTooltip, If } from '../utils/conditionalComponents';
import { distributeHorizontally } from '../utils/linearLayouts';
import { RouteOptimizerAdminIcon } from './RouteOptimizerAdminIcon';
import { ScheduleIcon } from './ScheduleIcon';

// The navigation drawer works entirely differently on mobile and desktop.
//
// On desktop, there is no top app bar. There is just the navigation drawer
//  on the left side. It is always visible. In its “closed” state, it shows
//  only the page icons. In its “open” state, it shows page icons and labels.
//
// On mobile, we have an app bar. It has a button that lets you toggle the
//  navigation drawer. In its “closed” state, the navigation drawer is entirely
//  hidden. In its “open” state, it shows page icons and labels. The user
//  can also swipe the drawer open from the left.
//
// On desktop, we have two options for how the navigation drawer can work.
//  If PERSISTENT_DRAWER_IN_DESKTOP is true, then the navigation drawer is
//  can be left open/expanded. The page content will accommodate the expanded
//  navigation drawer, and using the navigation drawer to navigate to a different
//  page won't collapse the navigation drawer. If PERSISTENT_DRAWER_IN_DESKTOP
//  if false, then the navigation drawer cannot be left open/expanded. Clicking
//  any link in the navigation drawer will collapse it. Also, the page content
//  won't accommodate the expanded navigation drawer. The drawer will slide
//  "over" the page content.

// This is a static configuration variable. Feel free to change as our product evolves
const PERSISTENT_DRAWER_IN_DESKTOP = true;

// This tracks whether the drawer is open.
const isDrawerOpenAtom = atom<boolean>(false);

const appBarHeightMobile = '56px'; // On mobile, the app bar is always visible.
const appBarHeightDesktop = '0px'; // On desktop, there is no app bar.

// This function tells other components how much space to reserve for the app bar.
export const useReservedAppBarHeight = () =>
    useIsMobile() ? appBarHeightMobile : appBarHeightDesktop;

const closedNavDrawerWidthMobile = '0px'; // On mobile, the drawer is entirely hidden if closed.
const closedNavDrawerWidthDesktop = '60px'; // On desktop, the drawer has limited with if closed.
const openNavDrawerWidth = '300px'; // The drawer has full width if open, on all screen sizes.

// This function tells other components how much space to reserve for the navigation drawer.
export const useReservedNavDrawerWidth = (): string => {
    const isMobile = useIsMobile();
    const isDrawerOpen = useAtomValue(isDrawerOpenAtom);

    // On mobile, we don't reserve any space.
    if (isMobile) {
        return closedNavDrawerWidthMobile;
    }

    // On desktop, we only reserve space for the full expanded width if
    //  PERSISTENT_DRAWER_IN_DESKTOP is true and the drawer is open.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (isDrawerOpen && PERSISTENT_DRAWER_IN_DESKTOP) {
        return openNavDrawerWidth;
    }

    // Otherwise, we reserve space for the limited width desktop navigation drawer.
    return closedNavDrawerWidthDesktop;
};

const DrawerHeader = (): ReturnComponent => {
    const theme = useTheme();
    const [isDrawerOpen, openDrawer, closeDrawer] = useBooleanStateAtom(isDrawerOpenAtom);

    return (
        // We use opacity to control visibility, so it animates smoothly.
        <Box style={{ transition: useTransition(['opacity']) }}>
            {/* This is the tiny Axle Health icon in closed mode. */}
            <Box {...centerChildren} height={appBarHeightMobile} minHeight={appBarHeightMobile}>
                <IconButton
                    onClick={openDrawer}
                    aria-label='Open navigation drawer'
                    aria-expanded={isDrawerOpen}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <img
                        src={axleIconSrc}
                        alt={'Axle Health logo'}
                        style={{
                            // Conditional visibility.
                            opacity: !isDrawerOpen ? 1 : 0,
                            zIndex: !isDrawerOpen ? 1 : 0,
                            // Spacing.
                            width: '24px',
                            height: '24px',
                        }}
                    />
                </IconButton>
            </Box>
            {/* This is the wide Axle Health logo in open mode. */}
            <Box
                {...distributeHorizontally}
                height={appBarHeightMobile}
                style={{
                    // Conditional visibility.
                    opacity: isDrawerOpen ? 1 : 0,
                    // Positioning.
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    right: 0,
                    // Spacing.
                    padding: theme.spacing(1),
                    paddingLeft: theme.spacing(2),
                }}
            >
                <img
                    src={axleLogoSrc}
                    alt={'Axle Health logo'}
                    style={{
                        height: '100%',
                    }}
                />
                <IconButton
                    onClick={closeDrawer}
                    aria-label='Close navigation drawer'
                    aria-expanded={isDrawerOpen}
                >
                    <ChevronLeft />
                </IconButton>
            </Box>
        </Box>
    );
};

// Q: Why not just call this type alias `DrawerItem`?
// A: ESLint complains about the name conflicting with the React component of
//    the same name below, even though technically, TypeScript allows a type
//    and function or variable of the same name to co-exist.
type DrawerItemBaseConfig = {
    label: string;
    subLabel?: string;
    icon: DefiniteComponent;
    canAccess: boolean;
    exact?: boolean;
};

type DrawerItemPathConfig = DrawerItemBaseConfig & {
    path: string;
    onClick?(event: MouseEvent): void;
};

type DrawerItemOnClickConfig = DrawerItemBaseConfig & {
    path?: string;
    onClick(event: MouseEvent): void;
};

type DrawerItemConfig = DrawerItemOnClickConfig | DrawerItemPathConfig;

type DrawerItemContentProps = {
    isDrawerOpen: boolean;
    item: DrawerItemConfig;
    onClick?(event: MouseEvent): void;
};

const DrawerItemContent: FC<DrawerItemContentProps> = (props) => {
    const { item, isDrawerOpen, onClick } = props;
    const location = useLocation();

    const isLinkSelected = useMemo(
        () =>
            typeof item.path !== 'undefined' &&
            (item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path)),
        [item.exact, item.path, location.pathname],
    );
    return (
        <ConditionalTooltip
            title={`${item.label}${item.subLabel ? ` (${item.subLabel})` : ''}`}
            condition={!isDrawerOpen}
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, -12],
                            },
                        },
                    ],
                },
            }}
            placement={'right'}
        >
            <ListItemButton
                onClick={onClick}
                sx={[
                    { pl: isDrawerOpen ? 4 : 2 },
                    {
                        ...(isLinkSelected
                            ? {
                                  backgroundColor: AXLE_TEAL,
                                  color: 'white',
                                  '& .MuiSvgIcon-root': {
                                      fill: 'white',
                                  },
                                  '&:hover': {
                                      backgroundColor: AXLE_TEAL,
                                  },
                                  '& svg > g': {
                                      fill: 'white !important',
                                  },
                              }
                            : {}),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                ]}
            >
                <ListItemIcon
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: '2px',
                        marginRight: '2px',
                    }}
                >
                    {/* Placing the <ConditionalTooltip/> outside on <ListItemIcon/> makes
                        the tooltip appear too far from the icon. */}
                    {item.icon}
                </ListItemIcon>
                <ListItemText
                    primary={<CommandFInvisibleText>{item.label}</CommandFInvisibleText>}
                    secondary={
                        item.subLabel ? (
                            <Typography
                                variant='caption'
                                component='div'
                                noWrap
                                title={item.subLabel}
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                <CommandFInvisibleText>{item.subLabel}</CommandFInvisibleText>
                            </Typography>
                        ) : undefined
                    }
                />
            </ListItemButton>
        </ConditionalTooltip>
    );
};

type DrawerItemProps = {
    item: DrawerItemConfig;
};

const DrawerItem: FC<DrawerItemProps> = (props) => {
    const { item } = props;

    const [isDrawerOpen, _, closeDrawer] = useBooleanStateAtom(isDrawerOpenAtom);
    const isMobile = useIsMobile();

    const onClick = useCallback(
        (event: MouseEvent) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (isMobile || !PERSISTENT_DRAWER_IN_DESKTOP) {
                closeDrawer();
            }

            if (typeof item.onClick === 'function') {
                item.onClick(event);
            }
        },
        [closeDrawer, isMobile, item],
    );

    if (typeof item.path === 'undefined') {
        return <DrawerItemContent item={item} isDrawerOpen={isDrawerOpen} onClick={onClick} />;
    }

    return (
        <Link
            to={item.path}
            onClick={onClick}
            style={{
                color: '#000000',
                textDecoration: 'none',
            }}
        >
            <DrawerItemContent item={item} isDrawerOpen={isDrawerOpen} />
        </Link>
    );
};

type DrawerGroupConfig = {
    groupTitle: string;
    items: DrawerItemConfig[];
};

export const DrawerGroup = ({ group }: { group: DrawerGroupConfig }): ReturnComponent => {
    const [isDrawerOpen] = useBooleanStateAtom(isDrawerOpenAtom);
    const [isGroupOpen, openGroup, closeGroup] = useBooleanState(true);
    const animatedVisibility = useAnimatedVisibility(isDrawerOpen);

    const accessibleItems = group.items.filter((item) => item.canAccess);
    if (accessibleItems.length === 0) {
        return null;
    }

    return (
        <>
            {/* When the drawer is closed, don't show section labels. */}
            <ListItemButton
                onClick={isGroupOpen ? closeGroup : openGroup}
                sx={{
                    // Q: What does the `& > *` selector do?
                    // A: It applies the styles to all direct children of this component.
                    // Q: Why do we do that?
                    // A: Because otherwise, the child components would not end up having 0 height,
                    //    as you'd expect based on the child. This meant when the drawer was collapsed,
                    //    the section labels would not be visible, but still be clickable, causing
                    //    users confusion.
                    ...animatedVisibility,
                    '& > *': animatedVisibility,
                }}
            >
                <ListItemText>
                    <CommandFInvisibleText>{group.groupTitle}</CommandFInvisibleText>
                </ListItemText>
                {isGroupOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            {/* If the drawer is closed, and the group is collapsed, don't show the icons. */}
            <If condition={isDrawerOpen || isGroupOpen}>
                <Collapse
                    // If the drawer is closed, show all groups expanded.
                    in={isGroupOpen}
                    // Calculate transition time automatically.
                    timeout={'auto'}
                >
                    <List
                        // The default is to use <li> component, which feels weird; let's use <div/>
                        component={'div'}
                        // Disable padding. We'll apply our own.
                        disablePadding={true}
                    >
                        {accessibleItems.map((item) => (
                            <DrawerItem key={item.label} item={item} />
                        ))}
                    </List>
                </Collapse>
            </If>
        </>
    );
};

const DrawerItems = (): ReturnComponent => {
    const appConfig = useAppConfig();
    const showDemandConnections = useShowDemandConnections();
    const showSupplyConnections = useShowSupplyConnections();

    // Feature flags
    const hasDashboardFeature = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_HOME_VIEW);
    const hasOrdersFeature = useFeatureFlagEnabled(FeatureFlag.ORDERS);

    const { currentUserEmail: currentUserEmailDisplay } = useUserEmails();

    const logout = useLogout();

    const DRAWER_GROUPS: DrawerGroupConfig[] = [
        {
            groupTitle: 'Manage Visits',
            items: [
                // TODO(jessica): Finalizing location when we reorganize the nav drawer.
                {
                    label: 'Dashboard',
                    path: '/',
                    exact: true,
                    icon: <Monitor />,
                    canAccess: hasDashboardFeature,
                },
                {
                    label: 'Visits',
                    path: '/visits',
                    icon: <DateRange />,
                    canAccess: appConfig.visit.page,
                },
                hasOrdersFeature
                    ? {
                          label: 'Orders',
                          path: '/orders',
                          icon: <FormatListNumbered />,
                          canAccess: appConfig.orders.view,
                      }
                    : {
                          label: 'Visit Orders',
                          path: '/visit-orders',
                          icon: <FormatListNumbered />,
                          canAccess: appConfig.service_orders.page,
                      },
                {
                    label: 'Route Optimizer',
                    path: '/route-optimizer',
                    exact: true,
                    icon: <Route />,
                    canAccess: appConfig.route_optimizer.page,
                },
                {
                    label: 'Route Optimizer Admin',
                    path: '/route-optimizer/admin',
                    icon: <RouteOptimizerAdminIcon />,
                    canAccess: appConfig.route_optimizer.preview_clinician_view,
                },
                {
                    label: 'Book Visits',
                    path: '/booking',
                    icon: <PostAdd />,
                    canAccess:
                        appConfig.scheduling.page && appConfig.patient.edit && !hasOrdersFeature, // This page requires creating a patient so we should only show it if patient edit is enabled
                },
            ],
        },
        {
            groupTitle: 'Manage Patients',
            items: [
                {
                    label: 'Patients',
                    path: '/patients',
                    icon: <People />,
                    canAccess: appConfig.patient.page,
                },
                {
                    label: 'Patient Intake',
                    path: '/patient-intake',
                    icon: <Assignment />,
                    canAccess: appConfig.orders.view,
                },
            ],
        },
        {
            groupTitle: 'Manage Clinicians',
            items: [
                {
                    label: 'Clinicians',
                    path: '/clinicians',
                    icon: (
                        // The '1em' height and width match the other icons.
                        // The padding gives a bit more space around the icon.
                        <SvgIcon height={'1em'} width={'1em'} padding={'3px'} fillColor={'gray'}>
                            <ProfessionalIcon />
                        </SvgIcon>
                    ),
                    canAccess: appConfig.qhp.page || showSupplyConnections,
                },
                {
                    label: 'Clinician Schedules',
                    path: '/schedules',
                    icon: <ScheduleIcon />,
                    canAccess: appConfig.shift.page,
                },
            ],
        },
        {
            groupTitle: 'Admin Tools',
            items: [
                {
                    label: 'Territories',
                    path: '/territories',
                    icon: <Map />,
                    canAccess: appConfig.territories.page,
                },
                {
                    label: 'Services',
                    path: '/services',
                    icon: <LocalHospital />,
                    // Hide the page for demand users for now until we make the
                    // page more useful for their use cases.
                    canAccess: appConfig.service.page || showDemandConnections,
                },
                {
                    label: 'Service Areas',
                    path: '/service-areas',
                    icon: <PinDrop />,
                    canAccess: appConfig.service_area.page,
                },
                {
                    label: 'Operating Hour Blocks',
                    path: '/blocks',
                    icon: <EventBusy />,
                    canAccess: appConfig.operating_hour_block.page,
                },
                {
                    label: 'Report Builder',
                    path: '/reports',
                    icon: <CloudDownloadIcon />,
                    canAccess: appConfig.reports.page,
                },
                {
                    label: 'Insights',
                    path: '/insights',
                    icon: <QueryStatsIcon />,
                    canAccess: appConfig.insights.page,
                },
                {
                    label: 'User Settings',
                    path: '/user-settings',
                    icon: <ManageAccounts />,
                    canAccess: appConfig.user_settings.page,
                },
                {
                    label: 'Settings',
                    path: '/settings',
                    icon: <Settings />,
                    canAccess: appConfig.settings.page,
                },
            ],
        },
    ];

    return (
        <>
            {/* Drawer header to open/close it on desktop, and reserve space for app bar on mobile. */}
            <DrawerHeader />
            {/* Render the drawer groups */}
            <List
                // Let's use a more appropriate HTML component than <li>.
                component={'nav'}
                sx={{
                    // Use normal paper background.
                    bgcolor: 'background.paper',
                }}
            >
                {DRAWER_GROUPS.map((group) => (
                    <Fragment key={group.groupTitle}>
                        <Divider />
                        <DrawerGroup key={group.groupTitle} group={group} />
                    </Fragment>
                ))}
                <Divider />
                <DrawerItem
                    item={{
                        label: 'Logout',
                        subLabel: currentUserEmailDisplay ?? undefined,
                        icon: <ExitToApp />,
                        canAccess: true,
                        onClick: logout,
                    }}
                />
            </List>
        </>
    );
};

// The app bar that exists on mobile.
// It appears on top of the mobile navigation drawer.
const MobileAppBar = () => {
    const setIsDrawerOpen = useSetAtom(isDrawerOpenAtom);
    const toggleDrawer = () => setIsDrawerOpen((isOpen) => !isOpen);

    return (
        <AppBar
            position='fixed'
            sx={{
                zIndex: APP_BAR_Z_INDEX,
                backgroundColor: '#ffffff',
                height: appBarHeightMobile,
            }}
        >
            {/* Pin the toolbar to the app bar height: MUI's Toolbar mixin raises min-height to
                64px at the `sm` breakpoint (>=600px), which overflows this fixed 56px bar.
                !important beats that media-query rule across all breakpoints. */}
            <Toolbar sx={{ minHeight: `${appBarHeightMobile} !important` }}>
                <IconButton onClick={toggleDrawer} aria-label='Toggle navigation drawer'>
                    <img
                        src={axleIconSrc}
                        alt={'Axle Health logo'}
                        style={{
                            width: '30px',
                            height: '30px',
                            marginLeft: '-8px',
                        }}
                    />
                </IconButton>
            </Toolbar>
        </AppBar>
    );
};

const MobileNavigationDrawer = () => {
    const [isDrawerOpen, openDrawer, closeDrawer] = useBooleanStateAtom(isDrawerOpenAtom);

    // Offset the app root (#root) below the fixed app bar instead of wrapping content in a
    // box, keeping native scroll restoration (body stays the scroll container). The temporary
    // drawer overlays, so it reserves no horizontal space.
    useReserveRootNavOffset({ paddingTop: appBarHeightMobile });

    return (
        <>
            <MobileAppBar />
            <SwipeableDrawer
                anchor={'left'}
                open={isDrawerOpen}
                onOpen={openDrawer}
                onClose={closeDrawer}
            >
                <DrawerItems />
            </SwipeableDrawer>
        </>
    );
};

const DesktopNavigationDrawer = () => {
    const isDrawerOpen = useAtomValue(isDrawerOpenAtom);
    const width = isDrawerOpen ? openNavDrawerWidth : closedNavDrawerWidthDesktop;

    // Offset the app root (#root) instead of wrapping content in a box, keeping the layout
    // flat so the browser keeps native scroll restoration (body stays the scroll container).
    // The fixed drawer paper sits in this gutter.
    useReserveRootNavOffset({ paddingLeft: width });

    return (
        <Drawer
            variant={'permanent'}
            sx={{
                // Without the following property, some of the vertical spacing between icons
                // gets messed up, when the drawer is collapsed.
                whiteSpace: 'nowrap',
                // Width lives only on the fixed, full-height paper — never on the docked root,
                // which has height 0 (its only child, the paper, is position: fixed and out of
                // flow) and so cannot reserve layout space anyway.
                '& .MuiDrawer-paper': {
                    // Make the drawer not horizontally scrollable. It would be weird to allow that!
                    overflowX: 'hidden',
                    width,
                    transition: useTransition(['width']),
                },
            }}
        >
            <DrawerItems />
        </Drawer>
    );
};

export const NavigationDrawer = () =>
    useIsMobile() ? <MobileNavigationDrawer /> : <DesktopNavigationDrawer />;
